import BANOBAGI_CHANNEL_SKUS from "./banobagi.json";

// ชื่อประเภทสิทธิ์เป็นค่าชั่วคราว รอลูกค้า confirm UI ใหม่แล้วค่อยเปลี่ยนชื่อ
export const BANOBAGI_RIGHT_TYPES = Object.freeze({
  PP_KRIT_CONCERT: "pp-krit-concert"
});

export const BANOBAGI_RIGHT_DEFINITIONS = Object.freeze([
  {
    key: BANOBAGI_RIGHT_TYPES.PP_KRIT_CONCERT,
    label: "PP KRIT CONCERT"
  }
]);

export const BANOBAGI_RIGHT_RULES = Object.freeze({
  channelKey: "Lazada(BBT Cosmetics)",
  campaignSkus: Object.freeze(["B0691SC-SET", "B0690SC-SET"]),
  maxEligibleQuantityPerOrder: 5,
  bonusMinimumQuantity: 3,
  bonusMultiplier: 2,
  // วันเริ่มชั่วคราว รอวันแคมเปญจริง และยังไม่กำหนดวันสิ้นสุด
  rightStartDate: "2026-11-02",
  rightEndDate: null
});

const RIGHT_DEFINITION_BY_KEY = new Map(
  BANOBAGI_RIGHT_DEFINITIONS.map(definition => [definition.key, definition])
);

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toFiniteNumber(value, fallback = 0) {
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "") : value;
  const number = Number(normalizedValue);
  return Number.isFinite(number) ? number : fallback;
}

function createRight(key, value) {
  const definition = RIGHT_DEFINITION_BY_KEY.get(key);
  return {
    key,
    label: definition?.label || key,
    value
  };
}

function resolveChannelText(transaction) {
  return normalizeText(
    [
      transaction?.branch_name,
      transaction?.branchName,
      transaction?.sales_channel_name,
      transaction?.sale_channel_name,
      transaction?.channel_name,
      transaction?.marketplace_name
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function resolveProductCandidates(transaction, detail) {
  return [
    detail?.transaction_details,
    detail?.products,
    transaction?._detail?.products,
    transaction?._banobagiDetail?.transaction_details,
    transaction?.transaction_details,
    transaction?.products,
    transaction?.product_list,
    transaction?.product
  ].filter(Array.isArray);
}

// เลือก source เดียวที่มีสินค้าร่วมรายการจริง เพราะ list กับ detail ส่ง field
// ของสินค้าไม่เหมือนกัน และถ้ารวมทุก source จำนวนกล่องจะถูกนับซ้ำ
function resolveProductSourceWithCampaign(transaction, detail, skuSet) {
  const candidates = resolveProductCandidates(transaction, detail);
  const source = candidates.find(products =>
    products.some(product => isCampaignProduct(product, skuSet))
  );

  return source || [];
}

function normalizeSku(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

// ชื่อร้าน/marketplace ที่สาขาสะกดได้หลายแบบ
const CHANNEL_TOKEN_ALIASES = Object.freeze({
  tiktok: ["tiktok", "tik tok"],
  shopee: ["shopee", "ช้อปปี้"],
  lazada: ["lazada", "ลาซาด้า"],
  banobagi: ["banobagi", "บาโนบากิ"],
  bbt: ["bbt"]
});

function resolveTokenAliases(token) {
  return CHANNEL_TOKEN_ALIASES[token] || (token ? [token] : []);
}

function matchesAnyToken(channel, tokens) {
  return tokens.some(token => channel.includes(token));
}

// banobagi.json keys look like "Tiktok" or "Shopee(BBT Cosmetics)".
const CHANNEL_SKU_MATCHERS = Object.keys(BANOBAGI_CHANNEL_SKUS).map(key => {
  const openIndex = key.indexOf("(");
  const marketplace = openIndex === -1 ? key : key.slice(0, openIndex);
  const store =
    openIndex === -1 ? "" : key.slice(openIndex + 1).replace(")", "");

  return {
    key,
    marketplaceTokens: resolveTokenAliases(normalizeText(marketplace)),
    storeTokens: resolveTokenAliases(normalizeText(store).split(" ")[0] || "")
  };
});

const CHANNEL_SKU_SETS = new Map(
  Object.entries(BANOBAGI_CHANNEL_SKUS).map(([key, skus]) => [
    key,
    new Set((Array.isArray(skus) ? skus : []).map(normalizeSku))
  ])
);

// ชื่อสาขาจริงของแต่ละ channel เผื่อบิลส่งมาแค่ชื่อร้านโดยไม่มีชื่อ marketplace
// ต้องเทียบแบบชื่อยาวสุดชนะ เพราะ "bbt cosmetics" (Lazada) เป็นส่วนหนึ่งของ
// "bbt cosmetics flagship store" (Shopee) และ "banobagi" (Shopee) เป็นส่วนหนึ่ง
// ของ "banobagi thailand - cosmetic" (Tiktok)
const CHANNEL_SHOP_NAMES = Object.freeze({
  Tiktok: ["banobagi thailand - cosmetic", "banobagi thailand"],
  "Shopee(BBT Cosmetics)": ["bbt cosmetics flagship store"],
  "Shopee(BANOBAGI)": ["banobagi"],
  "Lazada(BBT Cosmetics)": ["bbt cosmetics"],
  "Lazada(BANOBAGI)": ["บาโนบากิ"]
});

function resolveShopNameChannelKeys(channel) {
  const matches = Object.entries(CHANNEL_SHOP_NAMES)
    .filter(([key]) => CHANNEL_SKU_SETS.has(key))
    .flatMap(([key, shopNames]) =>
      shopNames
        .filter(shopName => channel.includes(shopName))
        .map(shopName => ({ key, length: shopName.length }))
    );

  if (matches.length === 0) return [];

  const longestMatch = Math.max(...matches.map(match => match.length));

  return [
    ...new Set(
      matches
        .filter(match => match.length === longestMatch)
        .map(match => match.key)
    )
  ];
}

// Resolves which banobagi.json channels a bill belongs to. Falls back to the
// shop name when the marketplace is missing, and to every list of a marketplace
// when the marketplace is known but the shop is not.
function resolveChannelSkuKeys(transaction) {
  const channel = resolveChannelText(transaction);
  if (!channel) return [];

  const marketplaceMatches = CHANNEL_SKU_MATCHERS.filter(matcher =>
    matchesAnyToken(channel, matcher.marketplaceTokens)
  );
  if (marketplaceMatches.length === 0) {
    return resolveShopNameChannelKeys(channel);
  }

  const storeMatches = marketplaceMatches.filter(
    matcher =>
      matcher.storeTokens.length === 0 ||
      matchesAnyToken(channel, matcher.storeTokens)
  );

  return (storeMatches.length > 0 ? storeMatches : marketplaceMatches).map(
    matcher => matcher.key
  );
}

// สิทธิ์นับเฉพาะร้านที่ร่วมรายการ บิลที่ระบุร้านไม่ได้ชัดเจน (เช่นมีแค่คำว่า Lazada)
// จะได้ channel มากกว่าหนึ่งตัว และไม่นับสิทธิ์
function isCampaignChannelTransaction(transaction, rules) {
  const channelKeys = resolveChannelSkuKeys(transaction);
  return channelKeys.length === 1 && channelKeys[0] === rules.channelKey;
}

function resolveProductSkus(product) {
  return [
    product?.sku,
    product?.sku_code,
    product?.skuCode,
    product?.product_sku,
    product?.product_code,
    product?.productCode,
    product?.barcode,
    product?.article_no,
    product?.product_id,
    product?.id
  ]
    .filter(candidate => candidate !== null && candidate !== undefined)
    .map(normalizeSku)
    .filter(Boolean);
}

function isCampaignProduct(product, skuSet) {
  return resolveProductSkus(product).some(sku => skuSet.has(sku));
}

// Returns a comparable YYYYMMDD number, or null when the value is unusable.
function toDateNumber(value) {
  const text = String(value || "").trim();
  if (!text) return null;

  const isoParts = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const slashParts = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  let year;
  let month;
  let day;

  if (isoParts) {
    [, year, month, day] = isoParts;
  } else if (slashParts) {
    [, day, month, year] = slashParts;
  } else {
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  // Some endpoints answer with Buddhist years.
  const adYear = Number(year) >= 2500 ? Number(year) - 543 : Number(year);
  return adYear * 10000 + Number(month) * 100 + Number(day);
}

function resolveTransactionDateNumber(transaction, detail) {
  const candidates = [
    transaction?.transaction_date,
    transaction?.transaction_datetime,
    transaction?.transactionDate,
    detail?.transaction_date,
    transaction?._banobagiDetail?.transaction_date
  ];

  for (const candidate of candidates) {
    const dateNumber = toDateNumber(candidate);
    if (dateNumber !== null) return dateNumber;
  }

  return null;
}

// สิทธิ์นับเฉพาะบิลในช่วง rules.rightStartDate ถึง rules.rightEndDate
// (เทียบระดับวัน จึงครอบคลุมถึง 23:59:59 ของวันสุดท้าย)
// บิลที่ไม่มีวันที่หรืออ่านวันที่ไม่ได้ ถือว่าไม่เข้าเงื่อนไข
export function isWithinBanobagiRightPeriod(transaction, detail, rules) {
  const startDateNumber = toDateNumber(rules.rightStartDate);
  const endDateNumber = toDateNumber(rules.rightEndDate);
  if (startDateNumber === null && endDateNumber === null) return true;

  const dateNumber = resolveTransactionDateNumber(transaction, detail);
  if (dateNumber === null) return false;

  if (startDateNumber !== null && dateNumber < startDateNumber) return false;
  if (endDateNumber !== null && dateNumber > endDateNumber) return false;

  return true;
}

// คงเครื่องหมายไว้ เพราะบรรทัดจำนวนติดลบคือของคืน ต้องหักออกจากจำนวนกล่อง
function resolveProductQuantity(product) {
  const value = [product?.quantity, product?.qty].find(
    candidate => candidate !== null && candidate !== undefined
  );

  if (value === undefined || value === "") return null;
  return toFiniteNumber(value);
}

// list บางเส้นไม่ส่ง quantity ถ้าบิลมีสินค้าบรรทัดเดียว total_item คือจำนวนของบรรทัดนั้น
function resolveLineQuantity(product, products, transaction) {
  const quantity = resolveProductQuantity(product);
  if (quantity !== null) return quantity;
  if (products.length !== 1) return 1;

  return Math.abs(toFiniteNumber(transaction?.total_item)) || 1;
}

// บิลยกเลิกอาจส่งจำนวนเป็นค่าติดลบ จึงคิดจากจำนวนจริงแล้วค่อยใส่เครื่องหมายทีหลัง
function resolveCampaignQuantity(transaction, detail, rules) {
  const skuSet = new Set(rules.campaignSkus.map(normalizeSku));
  const products = resolveProductSourceWithCampaign(
    transaction,
    detail,
    skuSet
  );
  const quantity = products
    .filter(product => isCampaignProduct(product, skuSet))
    .reduce(
      (sum, product) =>
        sum + resolveLineQuantity(product, products, transaction),
      0
    );

  return isReversedTransaction(transaction) ? Math.abs(quantity) : quantity;
}

// ต่ำกว่า bonusMinimumQuantity ได้กล่องละ 1 สิทธิ์ ตั้งแต่ขั้นนั้นขึ้นไปได้ทั้งบิล x2
// และนับสูงสุด maxEligibleQuantityPerOrder กล่องต่อคำสั่งซื้อ (1, 2, 6, 8, 10)
function calculateRightCount(quantity, rules) {
  const eligibleQuantity = Math.min(
    Math.floor(quantity),
    rules.maxEligibleQuantityPerOrder
  );
  if (eligibleQuantity <= 0) return 0;

  return eligibleQuantity >= rules.bonusMinimumQuantity
    ? eligibleQuantity * rules.bonusMultiplier
    : eligibleQuantity;
}

export function createEmptyBanobagiRightsSummary() {
  return BANOBAGI_RIGHT_DEFINITIONS.reduce((summary, definition) => {
    summary[definition.key] = 0;
    return summary;
  }, {});
}

function isCancelledTransaction(transaction) {
  const statusId = transaction?.user_transaction_status_id;
  if (statusId === undefined || statusId === null || statusId === "") {
    return false;
  }
  return Number(statusId) !== 1;
}

// ค่า deleted จาก API มาได้หลายรูป (1, "1", true, "yes") และบาง export
// ส่ง "no" เป็น string สําหรับบิลปกติ จึงเทียบทุกรูปที่หมายถึง "ถูกลบ"
const DELETED_TRUTHY_VALUES = ["1", "true", "yes", "y"];

function isDeletedTransaction(transaction) {
  const raw = transaction?.deleted;
  if (raw === undefined || raw === null || raw === "") return false;
  return DELETED_TRUTHY_VALUES.includes(String(raw).trim().toLowerCase());
}

function isReversedTransaction(transaction) {
  return (
    isCancelledTransaction(transaction) || isDeletedTransaction(transaction)
  );
}

function applyCancellationSign(rights, transaction) {
  if (!isReversedTransaction(transaction)) return rights;

  return rights.map(right => ({
    ...right,
    value: -Math.abs(toFiniteNumber(right.value))
  }));
}

export function calculateBanobagiTransactionRights(
  transaction,
  detail = null,
  rules = BANOBAGI_RIGHT_RULES
) {
  if (!isWithinBanobagiRightPeriod(transaction, detail, rules)) return [];
  if (!isCampaignChannelTransaction(transaction, rules)) return [];

  const rightCount = calculateRightCount(
    resolveCampaignQuantity(transaction, detail, rules),
    rules
  );
  if (rightCount === 0) return [];

  return applyCancellationSign(
    [createRight(BANOBAGI_RIGHT_TYPES.PP_KRIT_CONCERT, rightCount)],
    transaction
  );
}

// อินวสเตอร์เดียวกันอาจมีหลาย transaction_id เมื่อ record เก่าถูก mark
// deleted แล้วออก record ใหม่มาแทน กรณีนี้ต้องนับใบที่ยังไม่ถูกลบ
// แล้วทิ้งใบที่ถูกลบไป ไม่ใช่เอา -1 ของใบเก่าไปหักใบใหม่จนเหลือ 0
// (เทียบด้วย invoice ไม่ใช่ transaction_date เพราะคู่ที่เจอมีเวลาต่างกันได้)
// ถ้าทุกใบใน invoice เดียวถูกลบหมด ให้คงค่าติดลบไว้ (claw back)
export function dropDeletedBillsWithActiveTwin(transactions) {
  const invoiceGroups = new Map();
  transactions.forEach(transaction => {
    const invoiceNo = transaction?.invoice_no;
    if (!invoiceNo) return;
    const key = transaction?.member_id + "||" + invoiceNo;
    if (!invoiceGroups.has(key)) invoiceGroups.set(key, []);
    invoiceGroups.get(key).push(transaction);
  });

  return transactions.filter(transaction => {
    if (!isDeletedTransaction(transaction)) return true;
    const invoiceNo = transaction?.invoice_no;
    if (!invoiceNo) return true;
    const group = invoiceGroups.get(transaction?.member_id + "||" + invoiceNo);
    return !group.some(other => !isDeletedTransaction(other));
  });
}

export function summarizeBanobagiRights(transactions) {
  return dropDeletedBillsWithActiveTwin(transactions).reduce(
    (summary, transaction) => {
      const rights = Array.isArray(transaction?._rights)
        ? transaction._rights
        : calculateBanobagiTransactionRights(
            transaction,
            transaction?._banobagiDetail || null
          );

      rights.forEach(right => {
        if (!Object.prototype.hasOwnProperty.call(summary, right.key)) return;
        summary[right.key] += toFiniteNumber(right.value);
      });
      return summary;
    },
    createEmptyBanobagiRightsSummary()
  );
}
